// Phase 2 strings: production bugs, RCA, postmortems, action items, learnings, regression suite.
const bugsEn = {
  severity: {
    s1: "S1 Critical",
    s2: "S2 Major",
    s3: "S3 Minor",
    s4: "S4 Trivial",
    short: { s1: "S1", s2: "S2", s3: "S3", s4: "S4" },
    rubric: {
      s1: "Core flow down, data loss, money or security impact, many users.",
      s2: "Important feature broken with no reasonable workaround, or many users affected.",
      s3: "Feature degraded, a workaround exists, or few users affected.",
      s4: "Cosmetic: text, layout, minor UX.",
    },
  },
  bugPriority: { p0: "P0", p1: "P1", p2: "P2", p3: "P3" },
  bugStatus: {
    reported: "Reported",
    triaged: "Triaged",
    investigating: "Investigating",
    mitigated: "Mitigated",
    fixed: "Fixed",
    verified: "Verified",
    analysis: "Analysis",
    closed: "Closed",
  },
  resolution: {
    fixed: "Fixed",
    duplicate: "Duplicate",
    not_a_bug: "Not a bug",
    wont_fix: "Won't fix",
  },
  detectedBy: {
    customer: "Customer report",
    support: "Support",
    internal: "Internal user",
    monitoring: "Monitoring / alert",
    qa: "QA",
  },
  rootCause: {
    requirements_misunderstood: "Requirements misunderstood / ambiguous spec",
    missing_requirement: "Missing requirement / edge case not considered",
    logic_error: "Logic error in code",
    integration: "Integration / third-party / API contract",
    data: "Data issue (bad data, migration)",
    configuration: "Configuration / deployment / environment",
    performance: "Performance / scalability",
    security: "Security",
    regression: "Regression (worked before, broken by a change)",
    infrastructure: "Infrastructure / hosting",
    other: "Other",
  },
  escapePoint: {
    requirements: "Requirements / grooming",
    design_review: "Design / technical review",
    code_review: "Code review",
    test_cases_missing: "Test cases — no case covered it",
    test_execution_missed: "Test execution — a case existed but testing missed it",
    environment_difference: "Test environment differed from production",
    monitoring: "Monitoring — we only found out from customers",
    not_preventable: "Not reasonably preventable",
  },
  actionKind: {
    regression_test: "Regression test",
    code_fix: "Code fix",
    monitoring: "Monitoring / alert",
    process: "Process / checklist",
    documentation: "Documentation",
    training: "Training",
  },
  actionStatus: {
    open: "Open",
    in_progress: "In progress",
    done: "Done",
    cancelled: "Cancelled",
  },
  postmortemStatus: {
    none: "Not started",
    draft: "Draft",
    approved: "Approved",
  },
  linkKind: {
    related: "Related",
    recurrence: "Recurrence",
  },
  bugNav: {
    bugs: "Bugs",
    regression: "Regression suite",
    learnings: "Learnings",
    openMajor: "{{count}} open S1/S2",
  },
  bugs: {
    overdue: "Overdue by {{time}}",
    dueIn: "Mitigate within {{time}}",
    title: "Production bugs",
    showMajor: "Show only S1/S2",
    majorCallout: "Open S1/S2: {{count}} — the number on the Bugs badge. Fix these first.",
    report: "Report bug",
    search: "Search bugs",
    filterOpen: "Open",
    filterAll: "All",
    filterClosed: "Closed",
    filterSeverity: "Severity",
    filterStatus: "Status",
    anyComponent: "Any component",
    anyApp: "Any app",
    mine: "Mine",
    empty: "No production bugs",
    emptyBody: "When something breaks after a release, report it here so it's tracked by severity and analysed.",
    noMatch: "No bugs match these filters.",
    openActions: "{{count}} open actions",
    detected: "Detected {{time}}",
    unowned: "No owner",
    bugN: "BUG-{{n}}",
  },
  report: {
    more: "More details",
    moreHint: "Priority, description, impact, timing, component, owner. All of it can be filled in later on the bug page.",
    sectionDetails: "Details",
    title: "Report a production bug",
    intro: "Describe what happened. Severity is about impact; priority is the order we fix in.",
    bugTitle: "What's wrong?",
    titleRequired: "Describe the problem in one line.",
    titlePlaceholder: "Card refunds over 1,000.00 fail with “Gateway error 502”",
    similarTitle: "Similar past bugs — is this a duplicate or a recurrence?",
    severity: "Severity",
    priority: "Priority",
    priorityAuto: "Defaults from severity ({{p}}). Change it only with a reason.",
    detectedBy: "Detected by",
    component: "Component",
    componentPlaceholder: "e.g. Refunds",
    client: "Client",
    clientPlaceholder: "Username",
    app: "App",
    foundIn: "Found in",
    description: "Description",
    steps: "Steps to reproduce",
    stepsHint: "Enter adds the next step; Enter on an empty step ends the list.",
    attachments: "Screenshots, videos or files",
    attachmentsHint: "Images, video, PDF, text, CSV, JSON or ZIP, up to 100 MB each. You can also paste a screenshot (Ctrl/⌘ V).",
    uploading: "Uploading {{done}}/{{total}}…",
    uploadFailed: "{{name}} couldn’t be uploaded. Attach it again on the bug page.",
    unsupportedFile: "{{name}}: this file type can’t be attached (images, video, PDF, text, CSV, JSON or ZIP).",
    expected: "Expected behaviour",
    actual: "Actual behaviour",
    impact: "Impact",
    startedAt: "Started (best guess)",
    detectedAt: "Detected",
    originTask: "Introduced by task",
    originHint: "The task whose release caused it, e.g. PAY-482.",
    owner: "Owner",
    submit: "Report bug",
    created: "BUG-{{n}} reported",
    sectionWhat: "What happened",
    sectionWhen: "When and where",
  },
  impact: {
    title: "Impact",
    users: "Users affected",
    customers: "Customers affected",
    money: "Money impact",
    moneyPlaceholder: "about 1,200,000 UZS refunded manually",
    data: "Data impact",
    workaround: "Workaround",
    none: "No impact recorded yet.",
  },
  bug: {
    stepOf: "Step {{n}} of {{total}}",
    nextStep: "next: {{status}}",
    liveUpdate: "{{name}} updated this bug — it's been refreshed.",
    moveTo: "Move to {{status}}",
    skipTo: "Skip to…",
    reopen: "Reopen",
    reopenTitle: "Reopen BUG-{{n}}?",
    reopenBody: "It goes back to Investigating. Say why, so the history makes sense.",
    reopenNote: "Why is it being reopened?",
    close: "Close",
    regressionCase: "Add regression case",
    regressionCreated: "Regression case {{key}} added to the regression suite",
    regressionLink: "Regression case {{key}}",
    statusChanged: "Moved to {{status}}",
    saved: "Saved",
    closedAs: "Closed · {{resolution}}",
    duplicateOf: "Duplicate of BUG-{{n}}",
    details: "Details",
    noDetails: "No details yet.",
    editDetails: "Edit details",
    properties: "Properties",
    reporter: "Reporter",
    owner: "Owner",
    originTask: "Introduced by",
    openTask: "Open task in Mixla",
    noValue: "—",
    activity: "Activity",
    noActivity: "No activity yet.",
    breadcrumb: "Bugs",
  },
  close: {
    title: "Close BUG-{{n}}",
    resolution: "Resolution",
    duplicateOf: "Duplicate of bug number",
    note: "Note",
    noteRequired: "A reason is required for this resolution.",
    blockersTitle: "Before closing as fixed:",
    submit: "Close bug",
    closed: "Bug closed",
    managersOnly: "Only project managers can close bugs.",
  },
  timeline: {
    edit: "Edit times",
    done: "Done",
    title: "Timeline",
    started: "Started",
    detected: "Detected",
    acknowledged: "Acknowledged",
    mitigated: "Mitigated",
    fixed: "Fixed",
    verified: "Verified",
    toDetect: "to detect",
    toAcknowledge: "to acknowledge",
    toMitigate: "to mitigate",
    toFix: "to fix",
    hint: "Times are stamped when the status changes. Correct them here if they're off.",
  },
  rca: {
    title: "Root-cause analysis",
    folded: "Not started. Open it once the cause is known.",
    blameless: "Blameless — ask what allowed it, not who did it.",
    category: "Root cause category",
    escape: "Escape point — where should we have caught it?",
    choose: "Choose…",
    rootCause: "Root cause",
    whys: "5 whys",
    whyN: "Why {{n}}",
    root: "Root",
    addWhy: "Add a why",
    lesson: "Lesson learned",
    lessonHint: "Only if there's a takeaway worth keeping. Shown in the Learnings library.",
  },
  postmortem: {
    title: "Postmortem",
    folded: "Not started. Write it once the bug is fixed.",
    required: "Required for S1/S2 before the bug can be closed.",
    summary: "Summary",
    timeline: "Timeline",
    trigger: "Trigger",
    wentWell: "What went well",
    wentWrong: "What went wrong",
    lucky: "Where we got lucky",
    approve: "Approve",
    approved: "Postmortem approved",
    approvedBy: "Approved by {{name}}",
    editResets: "Saving changes moves an approved postmortem back to draft.",
  },
  actions: {
    title: "Action items",
    empty: "No action items yet. Every serious bug should produce at least one.",
    newTitle: "What will we do?",
    kind: "Type",
    owner: "Owner",
    due: "Due",
    status: "Status",
    add: "Add",
    added: "Action item added",
    remove: "Remove action item",
    overdue: "Overdue",
    bug: "Bug",
  },
  comments: {
    feedTitle: "Discussion & activity",
    title: "Comments",
    empty: "No comments yet.",
    placeholder: "Write a comment…",
    send: "Comment",
  },
  attachmentsBug: {
    title: "Attachments",
    removed: "Attachment removed",
    uploaded: "File attached",
  },
  links: {
    title: "Linked bugs",
    empty: "No linked bugs.",
    number: "Bug number",
    add: "Link",
    remove: "Unlink BUG-{{n}}",
  },
  learnings: {
    title: "Learnings",
    tabLibrary: "Library",
    tabReview: "Review",
    intro: "Closed bugs and what we learned from them. Search before building something risky.",
    search: "Search lessons",
    anyCategory: "Any root cause",
    anyEscape: "Any escape point",
    empty: "No lessons yet. They appear here when bugs are closed with a lesson.",
    recurrences: "Recurred {{count}}×",
    closedOn: "Closed {{time}}",
  },
  review: {
    intro: "For the monthly bug review: what's open, what's overdue, what still needs analysis.",
    openBySeverity: "Open bugs by severity",
    recentMajor: "S1/S2 in the last 30 days",
    recentMajorEmpty: "No S1/S2 bugs in the last 30 days.",
    missingAnalysis: "Missing analysis",
    missingAnalysisEmpty: "Every fixed bug has a root cause.",
    openActions: "Open action items",
    openActionsEmpty: "No open action items.",
  },
  regression: {
    title: "Regression suite",
    intro: "Cases that guard against bugs coming back. Run them like any task, with rounds and the runner.",
    emptyTitle: "No regression suite yet",
    emptyBody: "It's created automatically when the first bug gets a regression case, or you can create it now.",
    create: "Create regression suite",
  },
  components: {
    title: "Components",
    intro: "Parts of the product bugs are filed under, e.g. Refunds, Checkout, Reports.",
    add: "Add component",
    placeholder: "New component",
    rename: "Rename {{name}}",
    remove: "Remove {{name}}",
    empty: "No components yet.",
    saved: "Components saved",
  },
  closeCheck: {
    title: "To close as fixed:",
    verified: "Fix verified",
    rootCause: "Root cause",
    escape: "Where it should have been caught",
    postmortem: "Postmortem approved",
    ready: "Ready to close",
  },
  sightings: {
    title: "Where it was seen",
    empty: "No sightings yet. Add one when someone sees this bug on a version.",
    seenAgain: "Seen again",
    record: "Record sighting",
    addAnother: "Add another",
    version: "Version",
    versionPlaceholder: "Search or type a version",
    versionTextPlaceholder: "Type a version",
    versionsFailed: "Couldn't load this app's versions. You can still type one.",
    useText: "Use text “{{text}}”",
    useTextHint: "No version will be created",
    linksTo: "This links to version {{name}}.",
    keptAsText: "Kept as text. It links by itself when this app gets a version with that name.",
    keptAsTextNoApp: "Kept as text. It can link once the project has an app with that version.",
    liveHint: "Leave it empty to use the version live at that time.",
    chooseApp: "Choose an app",
    noAppChosen: "Not sure",
    noApp: "No app",
    noApps: "This project has no apps yet, so a version can only be kept as text.",
    addApp: "Add an app",
    noAppsAsk: "This project has no apps yet, so a version can only be kept as text. A project manager can add one.",
    introducedIn: "Introduced in",
    versionUnknown: "Version unknown",
    inferred: "Inferred: live at that time",
    textOnly: "Text only",
    migrated: "From the old fields",
    reportedAs: "Reported as {{text}}",
    onFixVersion: "The bug is open, but this version holds a fix for it.",
    setApp: "Set app",
    makeVersion: "Make version {{name}}",
    outcome: {
      nothing: "",
      expected: "Expected",
      no_fix_here: "No fix in this app yet",
      cant_compare: "Can't compare, check by hand",
      evidence: "Added to a flag",
      flagged: "Flagged",
    },
    result: {
      nothing: "Sighting recorded.",
      expected: "Expected: this version comes before the fix.",
      no_fix_here: "Sighting recorded. There's no fix in this app yet.",
      cant_compare: "Sighting recorded. This version can't be compared with the fix, so check it by hand.",
      evidence: "This version should hold the fix. Added as evidence to the regression flag.",
      flagged: "Regression flagged: this version should hold the fix. The bug's owner, reporter and project managers were told.",
    },
    inferredResult: "It's linked to {{name}}, the version live at that time.",
    textResult: "“{{text}}” is kept as text until a version with that name appears.",
    bannerTitle: "Possible regression in {{app}}.",
    bannerBody: "Seen on {{version}}, which should hold the fix from {{fix}}.",
    bannerEvidence: "Later sightings: {{count}}.",
    reopen: "Reopen",
    dismiss: "Dismiss",
    reopened: "Bug reopened",
    dismissed: "Flag dismissed",
    reopenTitle: "Reopen for {{app}}",
    reopenBody: "The bug goes back to Investigating. This decides the open flags for {{apps}}, and the fixes in those apps stop counting.",
    dismissTitle: "Dismiss the flag for {{app}}",
    dismissBody: "The bug stays as it is. More sightings on this version only add evidence, and a higher version raises a new flag.",
    note: "Note",
    noteRequired: "Say why it's being reopened.",
    pastFlags: "Earlier flags",
    flagState: {
      open: "{{app}}: open",
      reopened: "{{app}}: reopened by {{name}}.",
      dismissed: "{{app}}: dismissed by {{name}}.",
    },
    dismissedUntil: "Holds until a version above {{version}} is seen.",
  },
  bugActivity: {
    kinds: {
      bug_reported: "reported the bug",
      bug_updated: "edited the bug",
      bug_status_changed: "changed the status",
      bug_release_changed: "changed the fix versions",
      bug_closed: "closed the bug",
      bug_reopened: "reopened the bug",
      postmortem_approved: "approved the postmortem",
      link_added: "linked a bug",
      link_removed: "unlinked a bug",
      action_item_added: "added an action item",
      action_item_updated: "updated an action item",
      action_item_removed: "removed an action item",
      comment_added: "commented",
      regression_case_created: "added a regression case",
      attachment_added: "attached a file",
      attachment_removed: "removed a file",
      sighting_added: "recorded a sighting",
      sighting_linked: "linked sightings to a version",
      sighting_app_set: "set a sighting's app",
      regression_flagged: "flagged a regression",
      regression_reopened: "reopened a regression",
      regression_dismissed: "dismissed a regression",
      regression_cleared: "cleared a regression that no longer applies",
      gitlab_link_added: "linked a merge request or branch",
      gitlab_link_confirmed: "confirmed a merge request or branch link",
      gitlab_link_removed: "removed a merge request or branch link",
      gitlab_fix_skipped_no_owner: "couldn't mark the bug fixed on merge, because it has no owner",
    },
  },
  inboxBugs: {
    title: "My bugs",
    empty: "No open bugs assigned to you.",
  },
};

export default bugsEn;

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
export type BugsTranslation = Shape<typeof bugsEn>;
